import type { ErrorCause } from "@lxc/app-device-common";
import type { ErrorContext } from "~/core/models/ErrorContext.enum";
import type { ErrorResponse } from "~/core/models/ErrorResponse.interface";
import type { ErrorResponseCode } from "~/core/models/ErrorResponseCode.interface";
import {
  NotificationKey,
  setDetailMessageAsListItem,
  showNotificationError,
} from "~/core/utils/notifications";
import { i18n } from "~/plugins/i18n";

const LOCALE_PREFIX_UNEXPECTED = "errors.httpStatusCodes.500";
const LOCALE_PREFIX_HTTP_STATUS_CODES = "errors.httpStatusCodes";

export default class LxcError implements ErrorResponse {
  public type: string;
  public title: string;
  public status: number;
  public detail: string;
  public instance: string;
  public codes?: ErrorResponseCode[];

  constructor(error: ErrorResponse) {
    this.type = error.type;
    this.title = error.title;
    this.status = error.status;
    this.detail = error.detail;
    this.instance = error.instance;
    this.codes = error.codes;
  }

  /**
   * Return true if the provided parameter is an error, false otherwise
   * @param error any
   * @returns true|false
   */
  static check(error: unknown): error is LxcError {
    return error instanceof LxcError;
  }

  /**
   * Show a notification about the error
   * @param titleKey title i18n translation key
   * @param context
   */
  public notify(titleKey?: NotificationKey, context?: ErrorContext) {
    const error: Error = this.toError(titleKey, context);

    // build the notification error title
    // - use the provided title key if the translation exists
    // - otherwise, use the error message
    const notificationErrorTitle =
      titleKey && i18n.global.te(titleKey)
        ? i18n.global.t(titleKey)
        : error.message;

    // build the notification error message
    // - if there is only one element in error values, use it in the message
    // - else if there are many elements in error values, use them as list in the message
    // - else if there is an error cause, use it in the message
    // - otherwise, use the title key in the error message if it is not already used in the error title
    const notificationErrorMessage = (error.cause as ErrorCause).values
      ? (error.cause as ErrorCause).values.length === 1
        ? (error.cause as ErrorCause).values[0]
        : (error.cause as ErrorCause).values
            .map(setDetailMessageAsListItem)
            .join("")
      : ((error.cause as unknown as string) ??
        (titleKey && i18n.global.te(titleKey) ? error.message : undefined));

    showNotificationError(notificationErrorTitle, notificationErrorMessage);
  }

  /**
   * Format an LxcError to Error
   * Build Error based on codes if it is not empty, otherwise build Error on status
   * @param titleKey title i18n translation key
   * @param context
   * @returns Error
   */
  public toError(titleKey?: string, context?: ErrorContext): Error {
    // case when codes defined and not empty => build error from codes
    if (this.codes?.length) {
      // build the error message
      // - use the provided title key if the translation exists
      // - else use the status translation if it exists
      // - otherwise, use the default error notification
      const errorMessage =
        titleKey && i18n.global.te(titleKey)
          ? i18n.global.t(titleKey)
          : i18n.global.te(
                `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}.title`,
              )
            ? i18n.global.t(
                `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}.title`,
              )
            : i18n.global.t(NotificationKey.error);

      // build the error options based on the error codes
      const errorOptions = {
        cause: {
          values: this.getDetailsTranslations(this.codes, context),
        },
      };

      return new Error(errorMessage, errorOptions);
    }
    // case when codes is undefined or empty
    else {
      // title and subtitle translations exist => build error from status
      if (
        i18n.global.te(
          `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}.title`,
        ) &&
        i18n.global.te(
          `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}.subtitle`,
        )
      ) {
        return new Error(
          i18n.global.t(
            `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}.title`,
          ),
          {
            cause: i18n.global.t(
              `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}.subtitle`,
            ),
          },
        );
      }
      // otherwise => build generic unexpected error
      else {
        return new Error(i18n.global.t(`${LOCALE_PREFIX_UNEXPECTED}.title`), {
          cause: i18n.global.t(`${LOCALE_PREFIX_UNEXPECTED}.subtitle`),
        });
      }
    }
  }

  /**
   * Build and return the translations for the error codes
   * Do not return translations for undefined keys
   * @returns
   */
  private getDetailsTranslations(
    codes: ErrorResponseCode[],
    context?: ErrorContext,
  ): string[] {
    const statusPrefix = `${LOCALE_PREFIX_HTTP_STATUS_CODES}.${this.status}`;
    const codesPrefix = `${statusPrefix}.codes`;

    return codes
      .map(({ code, params }) => {
        const codeKey = `${codesPrefix}.${code.toUpperCase()}`;

        // Try parameterized key first if params exist
        if (params) {
          const parameterizedKeysValuesKey = `${codeKey}_${Object.entries(
            params,
          )
            .sort(([keyA, keyB]) => keyA.localeCompare(keyB))
            .map(
              ([key, value]) =>
                `${key.toUpperCase()}_${value.toString().toUpperCase()}`,
            )
            .join("_")}`;

          // Try parameterized keys/values key with context
          if (context) {
            const parameterizedKeyWithContext = `${parameterizedKeysValuesKey}_${context}`;
            if (i18n.global.te(parameterizedKeyWithContext)) {
              // example: errors.httpStatuesCodes.409.codes.UNICITY_CONSTRAINT_FIELD_NAME_ORGANIZATION
              return i18n.global.t(parameterizedKeyWithContext, params);
            }
          }

          // Try parameterized keys/values key without context
          if (i18n.global.te(parameterizedKeysValuesKey)) {
            // example: errors.httpStatuesCodes.409.codes.UNICITY_CONSTRAINT_FIELD_NAME
            return i18n.global.t(parameterizedKeysValuesKey, params);
          }

          const parameterizedKeysKey = `${codeKey}_${Object.entries(params)
            .sort(([keyA, keyB]) => keyA.localeCompare(keyB))
            .map(([key]) => `${key.toUpperCase()}`)
            .join("_")}`;

          // Try parameterized keys key with context
          if (context) {
            const parameterizedKeyWithContext = `${parameterizedKeysKey}_${context}`;
            if (i18n.global.te(parameterizedKeyWithContext)) {
              // example: errors.httpStatuesCodes.409.codes.UNICITY_CONSTRAINT_FIELD_ORGANIZATION
              return i18n.global.t(parameterizedKeyWithContext, params);
            }
          }

          // Try parameterized keys key without context
          if (i18n.global.te(parameterizedKeysKey)) {
            // example: errors.httpStatuesCodes.409.codes.UNICITY_CONSTRAINT_FIELD
            return i18n.global.t(parameterizedKeysKey, params);
          }
        }

        // Try code key
        if (i18n.global.te(codeKey)) {
          // example: errors.httpStatuesCodes.409.codes.UNICITY_CONSTRAINT
          return i18n.global.t(codeKey, params);
        }

        // Fall back to status key
        return i18n.global.te(`${statusPrefix}.subtitle`)
          ? // example: errors.httpStatuesCodes.409.subtitle
            i18n.global.t(`${statusPrefix}.subtitle`, params)
          : "";
      })
      .filter((detailTranslation: string) => !!detailTranslation);
  }
}
